import { or, and, eq, like, sql } from 'drizzle-orm'
import { DB, DBNeonHttp, Entry, isDBNeonHttpResult } from './queries'
import { schema } from '.'
import { apiDefaultBranchRef } from '@basehub/shared-constants'
import {
  AtLeast,
  Branch,
  Commit,
  RootBlock,
  isBranch,
  isPlayground,
} from '../replicache'
import { invariant } from '@epic-web/invariant'

// SqlStorage types for Durable Objects
export type SqlStorageValue = ArrayBuffer | string | number | null
export interface SqlStorageCursor<T extends Record<string, SqlStorageValue>> {
  next():
    | {
        done?: false
        value: T
      }
    | {
        done: true
        value?: never
      }
  toArray(): T[]
  one(): T
  raw<U extends SqlStorageValue[]>(): IterableIterator<U>
  columnNames: string[]
  get rowsRead(): number
  get rowsWritten(): number
  [Symbol.iterator](): IterableIterator<T>
}

export interface SqlStorage {
  exec<T extends Record<string, SqlStorageValue>>(
    query: string,
    ...bindings: any[]
  ): SqlStorageCursor<T>
  get databaseSize(): number
}

export type ResolvedRef = null | Entry<Branch | Commit>

/**
 * Whether the playground branch in the row being filtered (its `value`
 * column) is still served: until `playgroundExpiresAt`, or for 5 days after
 * creation when that's unset. resolveRef ignores expired playgrounds.
 */
export const isUnexpiredPlaygroundSQL = (now: Date) => sql`(
  -- If playgroundExpiresAt is set, check if it's not expired
  (value->>'playgroundExpiresAt' IS NOT NULL AND (value->>'playgroundExpiresAt') > ${now.toISOString()})
  OR
  -- If playgroundExpiresAt is null, check default expiration (5 days from creation)
  (value->>'playgroundExpiresAt' IS NULL AND (value->>'createdAt') > ${new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString()})
)`

export const resolveRef = async ({
  ref,
  gitBranch,
  playgroundId,
  spaceID,
  db,
  cache,
}: {
  ref: string | null
  gitBranch: string | null
  playgroundId: string | null
  /**
   * Not implemented yet, but the idea is to link basehub commits to git commits, so deployments can be immutable.
   */
  gitCommitSHA: string | null
  db: DB | DBNeonHttp
  spaceID: string
  cache?: {
    revalidate?: boolean
    get: (key: string) => Promise<Entry<Branch | Commit>>
    set: (key: string, value: Entry<Branch | Commit>) => Promise<void>
  }
}): Promise<ResolvedRef> => {
  const cacheKey = `resolved-ref:${ref || apiDefaultBranchRef}`

  if (cache && !cache.revalidate) {
    const cached = await cache.get(cacheKey)
    if (cached) return cached
  }

  const isDefaultBranchQuery = !ref || ref === apiDefaultBranchRef

  if (playgroundId) {
    // retrieve branch by playgroundId where the playground is linked to the current branch (and value->>'sourceBranchId' = ${ref})
    const result = await db.execute<Entry<Branch>>(
      sql`
      with playground_branch as (
        select * from entry 
        where key = ${`branch/${playgroundId}`} 
        and spaceid = ${spaceID}
        and value->>'playgroundId' = ${playgroundId}
        and ${isUnexpiredPlaygroundSQL(new Date())}
      ),
      source_branch as (
        select * from entry 
        where key = 'branch/' || (select playground_branch.value->>'sourceBranchId' from playground_branch)
        and spaceid = ${spaceID}
      )
      select * from playground_branch
      where ${
        !ref || ref === apiDefaultBranchRef
          ? sql`TRUE`
          : sql`
        -- ref matches playground branch itself
        (playground_branch.value->>'name' = ${ref} or playground_branch.value->>'id' = ${ref})
        -- OR ref matches the source branch
        or EXISTS (
          select 1 from source_branch 
          where source_branch.value->>'name' = ${ref} 
          or source_branch.value->>'id' = ${ref}
        )
      `
      }
      `
    )
    let resolved: ResolvedRef | null = null
    if (isDBNeonHttpResult(result)) {
      if (result.rows[0]) resolved = result.rows[0] as Entry<Branch>
    } else if (result[0]) resolved = result[0] as Entry<Branch>
    if (resolved) {
      return { ...resolved, lastmodified: new Date(resolved.lastmodified) }
    }
  }

  const filters: Parameters<typeof and> = []

  if (isDefaultBranchQuery) {
    filters.push(eq(schema.entry.spaceid, spaceID))
    filters.push(like(schema.entry.key, `branch/%`))
    filters.push(
      or(
        sql`value->>'archivedAt' IS NULL`,
        sql`(value->>'archivedAt')::text = 'null'`
      )
    )
    filters.push(
      or(
        sql`(value->>'isDefault')::boolean IS TRUE`,
        gitBranch ? sql`value->'git'->>'branch' = ${gitBranch}` : undefined
      )
    )
  } else {
    // catch all query for branches and commits
    filters.push(
      or(
        and(
          eq(schema.entry.spaceid, spaceID),
          like(schema.entry.key, `branch/%`),
          or(
            sql`value->>'archivedAt' IS NULL`,
            sql`(value->>'archivedAt')::text = 'null'`
          ),
          sql`value->>'name' = ${ref}`
        ),
        and(
          eq(schema.entry.spaceid, spaceID),
          eq(schema.entry.key, `commit/${ref}`)
        )
      )
    )
  }

  const result = await (db as DB | DBNeonHttp)
    .select()
    .from(schema.entry)
    .where(and(...filters))
    .orderBy(schema.entry.key)
    .limit(2)

  invariant(result, 'Failed to get result from db')

  const resolved = result.reduce<ResolvedRef | null>((acc, curr) => {
    if (acc === null) return curr as ResolvedRef
    if (curr.key.startsWith('branch/')) {
      const currBranch = curr.value as Branch
      if (currBranch.git?.branch === gitBranch) {
        // prefer linked branch
        return curr as ResolvedRef
      }
    }
    return acc
  }, null)

  if (cache && resolved) {
    await cache.set(cacheKey, resolved)
  }

  return resolved
}

export function getResolvedRefGqlCacheId({
  rootBlock,
  resolvedRef,
}: {
  rootBlock: AtLeast<RootBlock, 'id' | 'isCommitted' | 'branchId'>
  resolvedRef: NonNullable<ResolvedRef>['value'] | null | undefined
}) {
  if (!resolvedRef) {
    const isDraftMode = !rootBlock.isCommitted
    return `${isDraftMode}:${rootBlock.branchId}`
  }
  if (isBranch(resolvedRef)) {
    const isDraftMode = resolvedRef.workingRootBlockId === rootBlock.id
    if (isPlayground(resolvedRef) && !resolvedRef.playgroundExpiresAt) {
      return `${isDraftMode}:${resolvedRef.sourceBranchId ?? resolvedRef.id}`
    }
    return `${isDraftMode}:${resolvedRef.id}`
  }
  return `${false}:${resolvedRef.id}`
}
